import type { TranslateLoader } from '@ngx-translate/core';
import { map, type Observable } from 'rxjs';

/** Block in `de.json` / `en.json`: `{ "PFAD.ZUM.SCHLUESSEL": "iOS-Fassung" }`. */
export const IOS_TEXT_OVERRIDES_KEY = 'IOS_TEXT_OVERRIDES';

type Uebersetzungen = Record<string, unknown>;

/**
 * Ersetzt auf iOS die Texte, die zum Kauf außerhalb der App auffordern.
 *
 * **Warum (24.09.2026):** Apple lehnt Apps ab, die Nutzer zu einem Kauf außerhalb des
 * In-App-Kaufs lenken (Guideline 3.1.1/3.1.3). Die Kauf-Knöpfe waren auf iOS längst
 * ausgeblendet, die TEXTE aber nicht: „Bitte wähle einen Plan", „noch 3 Tage zum Abonnieren",
 * „Upgrade auf den Plus-Plan …", „Kontoerstellung über den Webbrowser" — in Sperrbildschirm,
 * Banner, Toasts und Tooltips, rund 15 Stellen. Statt jede Stelle einzeln zu bedingen, ersetzt
 * der Loader die Schlüssel zentral; neue Verwendungen desselben Schlüssels sind damit
 * automatisch geschützt.
 *
 * ⚠️ Wer einen NEUEN kauf-lenkenden Text anlegt, trägt seine iOS-Fassung in
 * `IOS_TEXT_OVERRIDES` ein (beide Sprachen). Ein Pfad, der nicht existiert, wird ignoriert —
 * geprüft von `ios-text-overrides.vitest.spec.ts`.
 *
 * Reine Funktion: verändert die Eingabe nicht.
 */
export function wendeIosTexteAn(uebersetzungen: Uebersetzungen): Uebersetzungen {
  const ersatz = uebersetzungen?.[IOS_TEXT_OVERRIDES_KEY];
  if (!ersatz || typeof ersatz !== 'object') return uebersetzungen;

  const kopie: Uebersetzungen = structuredClone(uebersetzungen);
  for (const [pfad, text] of Object.entries(ersatz as Record<string, unknown>)) {
    if (typeof text !== 'string') continue;
    const teile = pfad.split('.');
    let knoten: Record<string, unknown> = kopie;
    let gefunden = true;
    for (const teil of teile.slice(0, -1)) {
      const weiter = knoten[teil];
      if (!weiter || typeof weiter !== 'object') {
        gefunden = false;
        break;
      }
      knoten = weiter as Record<string, unknown>;
    }
    const blatt = teile[teile.length - 1];
    if (gefunden && typeof knoten[blatt] === 'string') knoten[blatt] = text;
  }
  return kopie;
}

/** Hüllt einen Loader ein und wendet auf iOS `wendeIosTexteAn` auf jede Sprache an. */
export class IosTextOverrideLoader implements TranslateLoader {
  constructor(
    private readonly innen: TranslateLoader,
    private readonly istIos: boolean
  ) {}

  getTranslation(sprache: string): Observable<Uebersetzungen> {
    const quelle = this.innen.getTranslation(sprache) as Observable<Uebersetzungen>;
    return this.istIos ? quelle.pipe(map(wendeIosTexteAn)) : quelle;
  }
}
